/**
 * 스케줄 공지(SCHEDULE_NOTICE) 폼 섹션.
 */
import {
  createEmptyScheduleItem,
  getDefaultScheduleYearMonth,
  getScheduleDeadlineDisplayTags,
  getSchedulePharmaSelectionConstraints,
  type ScheduleFormItem,
  validateScheduleRegistrationItems,
} from '@/pages/board/announcementSchedulePharma';
import { SchedulePharmaSelectModal } from '@/pages/board/SchedulePharmaSelectModal';
import { DatePicker, MonthPicker } from '@/shared/components/DatePicker';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Field } from '@/shared/components/ui/form-field';
import { ToolbarRow } from '@/shared/components/ui/row';
import { Textarea } from '@/shared/components/ui/textarea';
import { useToast } from '@/shared/utils/toast';
import { Plus, Trash2, X } from 'lucide-react';
import React, { useState } from 'react';

export interface ScheduleFormState {
  registrationYearMonth: string;
  scheduleTopContent: string;
  scheduleBottomContent: string;
  registrationScheduleItems: ScheduleFormItem[];
}

export const createDefaultScheduleFormState = (): ScheduleFormState => ({
  registrationYearMonth: getDefaultScheduleYearMonth(),
  scheduleTopContent: '',
  scheduleBottomContent: '',
  registrationScheduleItems: [createEmptyScheduleItem()],
});

interface AnnouncementScheduleSectionProps {
  value: ScheduleFormState;
  onChange: (next: ScheduleFormState) => void;
  disabled?: boolean;
}

export const AnnouncementScheduleSection: React.FC<
  AnnouncementScheduleSectionProps
> = ({ value, onChange, disabled = false }) => {
  const toast = useToast();
  const [pharmaModalOpen, setPharmaModalOpen] = useState(false);
  const [activeScheduleIndex, setActiveScheduleIndex] = useState<number | null>(
    null,
  );

  const updateItem = (index: number, patch: Partial<ScheduleFormItem>) => {
    const nextItems = [...value.registrationScheduleItems];
    nextItems[index] = { ...nextItems[index], ...patch };
    onChange({ ...value, registrationScheduleItems: nextItems });
  };

  const handleAddItem = () => {
    onChange({
      ...value,
      registrationScheduleItems: [
        ...value.registrationScheduleItems,
        createEmptyScheduleItem(),
      ],
    });
  };

  const handleRemoveItem = (index: number) => {
    if (value.registrationScheduleItems.length <= 1) return;
    onChange({
      ...value,
      registrationScheduleItems: value.registrationScheduleItems.filter(
        (_, i) => i !== index,
      ),
    });
  };

  const openPharmaModal = (index: number) => {
    setActiveScheduleIndex(index);
    setPharmaModalOpen(true);
  };

  const handlePharmaConfirm = (selection: {
    pharmaSelectionType: ScheduleFormItem['pharmaSelectionType'];
    pharmaCompanies: ScheduleFormItem['pharmaCompanies'];
  }) => {
    if (activeScheduleIndex === null) return;

    const nextItems = [...value.registrationScheduleItems];
    nextItems[activeScheduleIndex] = {
      ...nextItems[activeScheduleIndex],
      pharmaSelectionType: selection.pharmaSelectionType,
      pharmaCompanies: selection.pharmaCompanies,
    };

    const validation = validateScheduleRegistrationItems(nextItems);
    if (!validation.valid) {
      toast.error(validation.message ?? '제약사 선택을 확인해 주세요.');
      return;
    }

    onChange({ ...value, registrationScheduleItems: nextItems });
    setActiveScheduleIndex(null);
  };

  const handleRemoveTag = (
    scheduleIndex: number,
    tag: ReturnType<typeof getScheduleDeadlineDisplayTags>[number],
  ) => {
    const item = value.registrationScheduleItems[scheduleIndex];
    if (!item) return;

    if (tag.pharmaCompanyId != null) {
      updateItem(scheduleIndex, {
        pharmaCompanies: item.pharmaCompanies.filter(
          (c) => c.pharmaCompanyId !== tag.pharmaCompanyId,
        ),
      });
      return;
    }

    updateItem(scheduleIndex, {
      pharmaSelectionType: 'SELECTED',
      pharmaCompanies: [],
    });
  };

  const activeItem =
    activeScheduleIndex !== null
      ? value.registrationScheduleItems[activeScheduleIndex]
      : undefined;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>일정 안내</CardTitle>
          {!disabled ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="ruda-button-outline"
              onClick={handleAddItem}
            >
              <Plus className="mr-2 h-4 w-4" />
              마감일 추가
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-6 pt-0">
          <Field label="등록년월" htmlFor="registration-year-month">
            <MonthPicker
              id="registration-year-month"
              className="w-48"
              disabled={disabled}
              value={value.registrationYearMonth.replace('-', '')}
              onChange={(v) =>
                onChange({
                  ...value,
                  registrationYearMonth:
                    v.length === 6
                      ? `${v.slice(0, 4)}-${v.slice(4, 6)}`
                      : '',
                })
              }
            />
          </Field>

          <Field label="상단 내용 입력(선택)" htmlFor="schedule-top">
            <Textarea
              id="schedule-top"
              rows={4}
              maxLength={1000}
              disabled={disabled}
              placeholder="상단에 표시할 내용을 입력하세요."
              value={value.scheduleTopContent}
              onChange={(e) =>
                onChange({ ...value, scheduleTopContent: e.target.value })
              }
            />
          </Field>

          {value.registrationScheduleItems.map((item, index) => {
            const displayTags = getScheduleDeadlineDisplayTags(item);
            const duplicateDates = value.registrationScheduleItems.filter(
              (other, otherIndex) =>
                otherIndex !== index &&
                other.deadlineDate &&
                other.deadlineDate === item.deadlineDate,
            );
            const hasDuplicate =
              duplicateDates.length > 0 && Boolean(item.deadlineDate);

            return (
              <div
                key={`schedule-item-${index}`}
                className="space-y-4 rounded-12 border border-ruda-border-light bg-ruda-grey-50 p-4"
              >
                <ToolbarRow justify="between" gap={8}>
                  <p className="text-sm font-bold leading-6 tracking-tight text-ruda-text-primary">
                    등록마감일 {index + 1}
                  </p>
                  {!disabled &&
                  value.registrationScheduleItems.length > 1 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveItem(index)}
                    >
                      <Trash2 className="h-4 w-4 text-ruda-error" />
                    </Button>
                  ) : null}
                </ToolbarRow>

                <Field
                  label="등록 마감일"
                  error={
                    hasDuplicate
                      ? '등록 마감일은 중복될 수 없습니다.'
                      : undefined
                  }
                >
                  <DatePicker
                    className="w-48"
                    disabled={disabled}
                    value={item.deadlineDate}
                    onChange={(v) => updateItem(index, { deadlineDate: v })}
                  />
                </Field>

                <Field label="제약사 목록">
                  <div className="flex flex-wrap items-start gap-2">
                    {!disabled ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="ruda-button-outline"
                        onClick={() => openPharmaModal(index)}
                      >
                        제약사 선택
                      </Button>
                    ) : null}
                    <div className="min-h-12 flex-1 rounded-12 border border-ruda-border-light bg-white p-3">
                      {displayTags.length === 0 ? (
                        <span className="text-sm tracking-tight text-ruda-text-tertiary">
                          선택된 제약사가 없습니다.
                        </span>
                      ) : (
                        <ToolbarRow gap={8}>
                          {displayTags.map((tag) => (
                            <Badge
                              key={tag.key}
                              variant="secondary"
                              className="gap-1 pr-1"
                            >
                              {tag.label}
                              {!disabled && tag.removable ? (
                                <button
                                  type="button"
                                  className="rounded-full p-0.5 hover:bg-black/5"
                                  onClick={() => handleRemoveTag(index, tag)}
                                  aria-label={`${tag.label} 제거`}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              ) : null}
                            </Badge>
                          ))}
                        </ToolbarRow>
                      )}
                    </div>
                  </div>
                </Field>
              </div>
            );
          })}

          <Field label="하단 내용 입력(선택)" htmlFor="schedule-bottom">
            <Textarea
              id="schedule-bottom"
              rows={4}
              maxLength={1000}
              disabled={disabled}
              placeholder="하단에 표시할 내용을 입력하세요."
              value={value.scheduleBottomContent}
              onChange={(e) =>
                onChange({ ...value, scheduleBottomContent: e.target.value })
              }
            />
          </Field>
        </CardContent>
      </Card>

      <SchedulePharmaSelectModal
        open={pharmaModalOpen}
        onOpenChange={setPharmaModalOpen}
        initialSelection={
          activeItem
            ? {
                pharmaSelectionType: activeItem.pharmaSelectionType,
                pharmaCompanies: activeItem.pharmaCompanies,
              }
            : undefined
        }
        selectionConstraints={
          activeScheduleIndex !== null
            ? getSchedulePharmaSelectionConstraints(
                value.registrationScheduleItems,
                activeScheduleIndex,
              )
            : undefined
        }
        onConfirm={handlePharmaConfirm}
      />
    </>
  );
};
